/* Copyright 2021, Milkdown by Mirone. */
import { css } from '@emotion/css';
import { createCmd, createCmdKey } from '@milkdown/core';
import { createNode, findSelectedNodeOfType } from '@milkdown/utils';
import katex from 'katex';
import { InputRule } from 'prosemirror-inputrules';
import { NodeSelection } from 'prosemirror-state';
export const ModifyInlineMath = createCmdKey();
export const mathInline = createNode((options, utils) => {
    var _a;
    const placeholder = Object.assign({ empty: '(empty)', error: '(error)' }, ((_a = options === null || options === void 0 ? void 0 : options.placeholder) !== null && _a !== void 0 ? _a : {}));
    const style = utils.getStyle(({ size, palette }) => {
        return css `
            font-size: unset;

            &.ProseMirror-selectednode {
                outline: none;
                border: ${size.lineWidth} solid ${palette('line')};
            }
        `;
    });
    const id = 'math_inline';
    return {
        id,
        schema: {
            group: 'inline',
            inline: true,
            atom: true,
            attrs: {
                value: {
                    default: '',
                },
            },
            parseDOM: [{ tag: 'span[data-type="mathInline"]' }],
            toDOM: () => ['span', { class: style, 'data-type': id }, 0],
        },
        parser: {
            match: (node) => node.type === 'inlineMath',
            runner: (state, node, type) => {
                const code = node.value;
                state.addNode(type, { value: code });
            },
        },
        serializer: {
            match: (node) => node.type.name === id,
            runner: (state, node) => {
                state.addNode('inlineMath', undefined, node.attrs.value);
            },
        },
        commands: (nodeType) => [
            createCmd(ModifyInlineMath, (value = '') => (state, dispatch) => {
                const node = findSelectedNodeOfType(state.selection, nodeType);
                if (!node)
                    return false;
                const { tr } = state;
                const _tr = tr.setNodeMarkup(node.pos, undefined, Object.assign(Object.assign({}, node.node.attrs), { value }));
                dispatch === null || dispatch === void 0 ? void 0 : dispatch(_tr.setSelection(NodeSelection.create(_tr.doc, node.pos)));
                return true;
            }),
        ],
        view: (_editor, _nodeType, node) => {
            let currentNode = node;
            const dom = document.createElement('span');
            if (style) {
                dom.classList.add(style);
            }
            const render = (code) => {
                try {
                    if (!code) {
                        dom.innerHTML = placeholder.empty;
                    }
                    else {
                        katex.render(code, dom);
                    }
                }
                catch (_a) {
                    dom.innerHTML = placeholder.error;
                }
            };
            render(node.attrs.value);
            return {
                dom,
                update: (updatedNode) => {
                    if (!updatedNode.sameMarkup(currentNode))
                        return false;
                    currentNode = updatedNode;
                    const newVal = updatedNode.attrs.value;
                    render(newVal);
                    return true;
                },
            };
        },
        inputRules: (nodeType) => [
            new InputRule(/\$(.+)\$/, (state, match, start, end) => {
                const $start = state.doc.resolve(start);
                const index = $start.index();
                const $end = state.doc.resolve(end);
                if (!$start.parent.canReplaceWith(index, $end.index(), nodeType)) {
                    return null;
                }
                const value = match[1];
                return state.tr.replaceRangeWith(start, end, nodeType.create({
                    value,
                }, nodeType.schema.text(value)));
            }),
        ],
    };
});
//# sourceMappingURL=index.js.map